import React from 'react';

// Stacked Row Module Component
export const StackedRowModule = ({ data = {}, onChange, isPreview = false, editingCell, setEditingCell, tempCellValue, setTempCellValue, onConfirm, onCancel, rowIndex }) => {
  // Initialize with at least one objective row if none exist
  const objectives = data.objectives || [{ text: '', score: '', value: '', critical: false }];

  const handleObjectiveChange = (index, field, value) => {
    if (onChange) {
      const newObjectives = [...objectives];
      newObjectives[index] = { ...newObjectives[index], [field]: value };
      onChange({ ...data, objectives: newObjectives });
    }
  };

  const addObjectiveRow = () => {
    if (onChange) {
      const newObjectives = [...objectives, { text: '', score: '', value: '', critical: false }];
      onChange({ ...data, objectives: newObjectives });
    }
  };

  const removeObjectiveRow = (index) => {
    if (objectives.length > 1 && onChange) {
      const newObjectives = objectives.filter((_, i) => i !== index);
      onChange({ ...data, objectives: newObjectives });
    }
  };

  const handleItemChange = (value) => {
    if (onChange) {
      onChange({ ...data, item: value });
    }
  };

  // Custom editable cell for item and objectives
  const CustomEditableCell = ({ fieldKey, value, placeholder, objectiveIndex = null }) => {
    const cellKey = objectiveIndex !== null ? `objective_${objectiveIndex}_${fieldKey}` : fieldKey;
    const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === cellKey;

    const handleDivClick = () => {
      if (!editingCell) {
        setEditingCell({ rowIndex, field: cellKey });
        setTempCellValue(value || '');
      }
    };

    const handleConfirm = () => {
      if (objectiveIndex !== null) {
        handleObjectiveChange(objectiveIndex, fieldKey, tempCellValue);
      } else {
        handleItemChange(tempCellValue);
      }
      onConfirm();
    };

    const handleCancel = () => {
      onCancel();
    };

    if (isEditing) {
      return (
        <div className="flex items-start gap-1 p-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg">
          <input 
            type="text"
            className="flex-1 p-1 w-full border-none rounded-md focus:outline-none focus:ring-0 text-sm"
            value={tempCellValue} 
            onChange={e => setTempCellValue(e.target.value)} 
            autoFocus 
            dir="ltr"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
          />
          <div className="flex gap-1">
            <button
              onClick={handleConfirm}
              className="p-0.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              title="Confirm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div 
        className={`w-full px-1 py-1 border border-gray-300 rounded cursor-pointer bg-white hover:bg-gray-100 hover:shadow-sm hover:border-gray-400 transition-all duration-150 ${editingCell ? 'pointer-events-none opacity-60' : ''}`}
        onClick={handleDivClick}
      >
        {value || <span className="text-gray-400 italic text-sm">{placeholder}</span>}
      </div>
    );
  };

  if (isPreview) {
    // Preview/Print view - render each objective as a separate table row
    return (
      <div className="w-full">
        {objectives.map((obj, index) => (
          <div key={index} className="flex w-full">
            {/* Item column - only show on first row */}
            {index === 0 && (
              <div 
                className="border border-gray-300 bg-gray-50 p-1 text-center font-medium flex items-center justify-center"
                style={{ 
                  width: '25%',
                  minHeight: `${objectives.length * 24}px`
                }}
              >
                {data.item || 'Item'}
              </div>
            )}
            
            {/* Spacer for subsequent rows */}
            {index > 0 && <div style={{ width: '25%' }}></div>}
            
            {/* Objective column */}
            <div className="border border-gray-300 p-1 bg-white" style={{ width: '75%' }}>
              {obj.text || `Objective ${index + 1}`}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Interactive edit view - render as a vertical list that spans the item/objective columns
  return (
    <div className="w-full">
      <div className="space-y-1">
        {objectives.map((obj, index) => (
          <div key={index} className="flex items-center gap-2">
            {/* Item field - only show on first row */}
            {index === 0 && (
              <div className="flex-none" style={{ width: '25%' }}>
                <CustomEditableCell
                  fieldKey="item"
                  value={data.item}
                  placeholder="Enter item..."
                />
              </div>
            )}
            
            {/* Spacer for subsequent rows */}
            {index > 0 && <div style={{ width: '25%' }}></div>}
            
            {/* Objective text */}
            <div className="flex-1">
              <CustomEditableCell
                fieldKey="text"
                value={obj.text}
                placeholder={`Objective ${index + 1}...`}
                objectiveIndex={index}
              />
            </div>
            
            {/* Remove button for additional objectives */}
            {objectives.length > 1 && (
              <button
                onClick={() => removeObjectiveRow(index)}
                className="flex-none text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-all duration-200"
                title="Remove objective"
                disabled={editingCell !== null}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        ))}
        
        {/* Add objective button */}
        <div className="mt-2">
          <button
            onClick={addObjectiveRow}
            className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs"
            disabled={editingCell !== null}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Objective
          </button>
        </div>
      </div>
    </div>
  );
};

// Mental Status Module Component
export const MentalStatusModule = ({ data = {}, onChange, isPreview = false, editingCell, setEditingCell, tempCellValue, setTempCellValue, onConfirm, onCancel, rowIndex }) => {
  const checkboxCategories = ['Person', 'Place', 'Time'];
  
  const handleCheckboxChange = (category, checked) => {
    if (onChange) {
      const newData = { ...data };
      newData[category] = checked;
      onChange(newData);
    }
  };

  const handleTextChange = (field, value) => {
    if (onChange) {
      const newData = { ...data };
      newData[field] = value;
      onChange(newData);
    }
  };

  // Create a custom editable cell for C/C text
  const CustomEditableCell = ({ fieldKey, value, placeholder }) => {
    const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === fieldKey;

    const handleDivClick = () => {
      if (!editingCell) {
        setEditingCell({ rowIndex, field: fieldKey });
        setTempCellValue(value || '');
      }
    };

    const handleConfirm = () => {
      handleTextChange(fieldKey, tempCellValue);
      onConfirm();
    };

    const handleCancel = () => {
      onCancel();
    };

    if (isEditing) {
      return (
        <div className="flex items-start gap-1 p-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg">
          <input 
            type="text"
            className="flex-1 p-1 w-full border-none rounded-md focus:outline-none focus:ring-0 text-sm"
            value={tempCellValue} 
            onChange={e => setTempCellValue(e.target.value)} 
            autoFocus 
            dir="ltr"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
          />
          <div className="flex gap-1">
            <button
              onClick={handleConfirm}
              className="p-0.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              title="Confirm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div 
        className={`flex-1 px-0.5 py-0 border border-gray-300 rounded cursor-pointer bg-white hover:bg-gray-100 hover:shadow-sm hover:border-gray-400 transition-all duration-150 ${editingCell ? 'pointer-events-none opacity-60' : ''}`}
        onClick={handleDivClick}
      >
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </div>
    );
  };

  if (isPreview) {
    // Preview/Print view - simplified checkboxes and text display
    return (
      <div className="space-y-1 custom-module-container">
        <div className="grid grid-cols-3 gap-1">
          {checkboxCategories.map(category => (
            <div key={category} className="border border-gray-300 rounded-lg p-1 bg-gray-50">
              <label className="flex items-center gap-1 text-xs">
                <div className={`w-4 h-4 border-2 rounded ${
                  data[category] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
                } flex items-center justify-center`}>
                  {data[category] && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <span className="font-medium">{category}</span>
              </label>
            </div>
          ))}
        </div>
        <div className="border border-gray-300 rounded-lg p-1 bg-gray-50">
          <div className="flex items-center gap-1 text-xs">
            <div className={`w-4 h-4 border-2 rounded ${
              data['C/C_checked'] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
            } flex items-center justify-center`}>
              {data['C/C_checked'] && (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <span className="font-medium">C/C:</span>
            {data['C/C_text'] && (
              <span className="ml-1 px-1 py-0 border border-gray-300 rounded bg-white text-xs">
                {data['C/C_text']}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Editor view - interactive checkboxes and text input
  return (
    <div className="space-y-0.5 p-0.5">
      <div className="grid grid-cols-3 gap-1">
        {checkboxCategories.map(category => (
          <div key={category} className="border border-gray-300 rounded-lg p-1 bg-gray-50">
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <div className={`w-4 h-4 border-2 rounded ${
                data[category] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
              } flex items-center justify-center cursor-pointer`}
              onClick={() => handleCheckboxChange(category, !data[category])}>
                {data[category] && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <span className="font-medium">{category}</span>
            </label>
          </div>
        ))}
      </div>
      <div className="border border-gray-300 rounded-lg p-1 bg-gray-50">
        <div className="flex items-center gap-1 text-xs">
          <div className={`w-4 h-4 border-2 rounded ${
            data['C/C_checked'] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
          } flex items-center justify-center cursor-pointer`}
          onClick={() => handleCheckboxChange('C/C_checked', !data['C/C_checked'])}>
            {data['C/C_checked'] && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <label className="font-medium min-w-max">C/C:</label>
          <CustomEditableCell 
            fieldKey="C/C_text"
            value={data['C/C_text']}
            placeholder="Enter chief complaint"
          />
        </div>
      </div>
    </div>
  );
};

// Vital Signs Module Component
export const VitalSignsModule = ({ data = {}, onChange, isPreview = false, editingCell, setEditingCell, tempCellValue, setTempCellValue, onConfirm, onCancel, rowIndex }) => {
  const vitalSigns = [
    'BP', 'HR', 'RR', 'LS', 'SPO2', 
    'PUPILS', 'BGL', 'JVD', 'P. Edema'
  ];
  
  const handleCheckboxChange = (vital, checked) => {
    if (onChange) {
      const newData = { ...data };
      newData[`${vital}_checked`] = checked;
      onChange(newData);
    }
  };

  const handleTextChange = (vital, value) => {
    if (onChange) {
      const newData = { ...data };
      newData[`${vital}_text`] = value;
      onChange(newData);
    }
  };

  // Create a custom editable cell for vital text inputs
  const VitalEditableCell = ({ vitalKey, value, placeholder }) => {
    const fieldKey = `${vitalKey}_text`;
    const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === fieldKey;

    const handleDivClick = (e) => {
      e.stopPropagation();
      if (!editingCell) {
        setEditingCell({ rowIndex, field: fieldKey });
        setTempCellValue(value || '');
      }
    };

    const handleConfirm = () => {
      handleTextChange(vitalKey, tempCellValue);
      onConfirm();
    };

    const handleCancel = () => {
      onCancel();
    };

    if (isEditing) {
      return (
        <div className="flex flex-col items-center gap-1 p-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg">
          <input 
            type="text"
            className="w-full p-1 border-none rounded-md text-xs text-center focus:outline-none focus:ring-0"
            value={tempCellValue} 
            onChange={e => setTempCellValue(e.target.value)} 
            autoFocus 
            dir="ltr"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            placeholder={placeholder}
          />
          <div className="flex gap-1">
            <button
              onClick={handleConfirm}
              className="p-0.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              title="Confirm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div 
        className={`w-full text-xs bg-transparent text-center cursor-pointer hover:bg-gray-200 transition-all duration-150 p-0.5 relative z-20 min-h-[18px] ${editingCell ? 'pointer-events-none opacity-60' : ''}`}
        onClick={handleDivClick}
      >
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </div>
    );
  };

  if (isPreview) {
    // Preview/Print view - 5 items per row, card-like layout with matching colors
    return (
      <div className="custom-module-container">
        <div className="bg-gray-100 rounded-lg">
          <h4 className="font-bold text-sm text-center text-gray-700">Vital Signs</h4>
          <div className="grid grid-cols-5 gap-x-1 gap-y-1">
            {vitalSigns.map(vital => (
              <div key={vital} className="relative">
                {/* Hidden checkbox that covers the entire card */}
                <input
                  type="checkbox"
                  checked={data[`${vital}_checked`] || false}
                  readOnly
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className="flex h-full min-h-[28px] rounded overflow-hidden">
                  {/* Left side - Checkbox (narrow) */}
                  <div className="w-7 flex items-center justify-center bg-gray-100 flex-shrink-0">
                    <div className={`w-4 h-4 border-2 rounded ${
                      data[`${vital}_checked`] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
                    } flex items-center justify-center`}>
                      {data[`${vital}_checked`] && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  
                  {/* Right side - Label and Value (wider) */}
                  <div className="flex flex-col flex-1">
                    {/* Top - Label with subheading color */}
                    <div className="flex-1 flex items-center justify-center bg-purple-50 px-1 border-r border-gray-300">
                      <span className="font-bold text-xs text-center text-gray-700">{vital}</span>
                    </div>
                    
                    {/* Bottom - Value */}
                    <div className="flex-1 flex items-center justify-center bg-gray-50 px-1 border-r border-gray-300">
                      <span className="text-xs text-center p-0.5 text-gray-700">
                        {data[`${vital}_text`] || ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Editor view - interactive checkboxes with text inputs, card-like layout with matching colors
  return (
    <div className="custom-module-container bg-gray-100 rounded-lg">
      <h4 className="font-bold text-sm text-center text-gray-700">Vital Signs</h4>
      <div className="grid grid-cols-5 gap-1">
        {vitalSigns.map(vital => (
          <div key={vital} className="relative">
            {/* Hidden checkbox that covers the entire card */}
            <input
              type="checkbox"
              checked={data[`${vital}_checked`] || false}
              onChange={(e) => handleCheckboxChange(vital, e.target.checked)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            
            <div className="flex h-full min-h-[0px] rounded overflow-hidden">
              {/* Left side - Checkbox (narrow) */}
              <div className="px-3 flex items-center justify-center bg-gray-100 flex-shrink-0">
                <div className={`w-5 h-5 border-2 rounded ${
                  data[`${vital}_checked`] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
                } flex items-center justify-center`}>
                  {data[`${vital}_checked`] && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              
              {/* Right side - Label and Value (wider) */}
              <div className="flex flex-col flex-1">
                {/* Top - Label with subheading color */}
                <div className="flex-1 flex items-center justify-center bg-purple-50 px-1 border-r border-gray-300">
                  <label className="font-bold text-xs text-center text-gray-700">{vital}</label>
                </div>
                
                {/* Bottom - Value Input */}
                <div className="flex-1 flex items-center justify-center px-1 py-0.5 bg-gray-50 border-r border-gray-300">
                  <VitalEditableCell 
                    vitalKey={vital}
                    value={data[`${vital}_text`]}
                    placeholder="Value"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Subheading Module Component
export const SubheadingModule = ({ data = {}, onChange, isPreview = false, editingCell, setEditingCell, tempCellValue, setTempCellValue, onConfirm, onCancel, rowIndex }) => {
  const handleTextChange = (field, value) => {
    if (onChange) {
      const newData = { ...data };
      newData[field] = value;
      onChange(newData);
    }
  };

  // Custom editable cell for subheading text
  const CustomEditableCell = ({ fieldKey, value, placeholder }) => {
    const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === fieldKey;

    const handleDivClick = () => {
      if (!editingCell) {
        setEditingCell({ rowIndex, field: fieldKey });
        setTempCellValue(value || '');
      }
    };

    const handleConfirm = () => {
      handleTextChange(fieldKey, tempCellValue);
      onConfirm();
    };

    const handleCancel = () => {
      onCancel();
    };

    if (isEditing) {
      return (
        <div className="flex items-start gap-1 p-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg">
          <input 
            type="text"
            className="flex-1 p-1 w-full border-none rounded-md focus:outline-none focus:ring-0 text-sm font-semibold text-purple-800"
            value={tempCellValue} 
            onChange={e => setTempCellValue(e.target.value)} 
            autoFocus 
            placeholder={placeholder}
            dir="ltr"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
          />
          <div className="flex gap-1">
            <button
              onClick={handleConfirm}
              className="p-0.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              title="Confirm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div 
        className="w-full min-h-[1.5rem] px-2 py-1 rounded cursor-pointer hover:bg-purple-100 hover:shadow-sm border border-transparent hover:border-purple-300 transition-all duration-150 font-semibold text-purple-800 text-center"
        onClick={handleDivClick}
      >
        {value || <span className="text-purple-400 italic">{placeholder}</span>}
      </div>
    );
  };

  return (
    <div className="w-full bg-purple-50 p-1 rounded-lg border border-purple-200">
      <CustomEditableCell
        fieldKey="subheading_text"
        value={data.subheading_text}
        placeholder="Enter subheading text..."
      />
    </div>
  );
};

// Combined OPQRST & SAMPLE Module Component
export const OPQRSTSAMPLEModule = ({ data = {}, onChange, isPreview = false, editingCell, setEditingCell, tempCellValue, setTempCellValue, onConfirm, onCancel, rowIndex }) => {
  const opqrstFields = [
    { key: 'O', label: 'Onset', placeholder: 'When did it start?' },
    { key: 'P', label: 'Provocation', placeholder: 'Better/worse?' },
    { key: 'Q', label: 'Quality', placeholder: 'Describe it' },
    { key: 'R', label: 'Radiation', placeholder: 'Does it spread?' },
    { key: 'S', label: 'Severity', placeholder: 'Rate 1-10' },
    { key: 'T', label: 'Timing', placeholder: 'How long?' }
  ];

  const sampleFields = [
    { key: 'S_sample', label: 'Signs/Symptoms', placeholder: 'What are they?' },
    { key: 'A', label: 'Allergies', placeholder: 'To what?' },
    { key: 'M', label: 'Medications', placeholder: 'What kind?' },
    { key: 'P_past', label: 'Past History', placeholder: 'Relevant history?' },
    { key: 'L', label: 'Last Oral Intake', placeholder: 'What/when?' },
    { key: 'E', label: 'Events', placeholder: 'Leading up to this?' }
  ];

  const handleCheckboxChange = (key, checked) => {
    if (onChange) {
      const newData = { ...data, [`${key}_checked`]: checked };
      onChange(newData);
    }
  };

  const handleTextChange = (key, value) => {
    if (onChange) {
      const newData = { ...data, [`${key}_text`]: value };
      onChange(newData);
    }
  };

  const EditableCell = ({ fieldKey, value, placeholder }) => {
    const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === fieldKey;

    const handleDivClick = (e) => {
      e.stopPropagation();
      if (!editingCell) {
        setEditingCell({ rowIndex, field: fieldKey });
        setTempCellValue(value || '');
      }
    };

    const handleConfirm = () => {
      handleTextChange(fieldKey.replace('_text', ''), tempCellValue);
      onConfirm();
    };

    const handleCancel = () => {
      onCancel();
    };

    if (isEditing) {
      return (
        <div className="flex items-center gap-1 p-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg z-30">
          <input
            type="text"
            className="w-full p-1 border-none rounded-md text-xs focus:outline-none focus:ring-0"
            value={tempCellValue}
            onChange={e => setTempCellValue(e.target.value)}
            autoFocus
            dir="ltr"
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); }
              if (e.key === 'Escape') { e.preventDefault(); handleCancel(); }
            }}
            placeholder={placeholder}
          />
          <div className="flex gap-1">
            <button onClick={handleConfirm} className="p-0.5 bg-green-500 text-white rounded-full hover:bg-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button onClick={handleCancel} className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`w-full text-xs rounded bg-white text-left cursor-pointer hover:bg-gray-100 p-0.5 transition-all min-h-[18px] ${editingCell ? 'pointer-events-none opacity-60' : ''}`}
        onClick={handleDivClick}
      >
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </div>
    );
  };

  const renderFields = (fields, title) => (
    <div className="bg-gray-100 rounded-lg">
      <h4 className="font-bold text-sm text-center text-gray-700">{title}</h4>
      <div className="space-y-0.5">
        {fields.map(({ key, label, placeholder }) => (
          <div key={key} className="relative bg-white">
            <div className="grid grid-cols-12 gap-1 items-center min-h-[24px] rounded-lg overflow-hidden">
              <div className="col-span-1 flex items-center justify-center bg-gray-50 h-full border-r border-gray-300 relative">
                <input
                  type="checkbox"
                  checked={data[`${key}_checked`] || false}
                  onChange={(e) => !isPreview && handleCheckboxChange(key, e.target.checked)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  disabled={isPreview}
                />
                <div className={`w-4 h-4 border-2 rounded ${data[`${key}_checked`] ? 'bg-blue-500 border-blue-500' : 'border-gray-400'} flex items-center justify-center`}>
                  {data[`${key}_checked`] && (
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="col-span-4 flex items-center justify-start bg-gray-50 h-full border-r border-gray-300">
                <span className="font-medium text-xs px-1 text-left">{label}</span>
              </div>
              <div className="col-span-7 flex items-center justify-center p-0.5 bg-white h-full">
                {isPreview ? (
                  <span className="text-xs text-left w-full px-0.5">{data[`${key}_text`] || ''}</span>
                ) : (
                  <EditableCell
                    fieldKey={`${key}_text`}
                    value={data[`${key}_text`]}
                    placeholder={placeholder}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`custom-module-container ${isPreview ? '' : 'bg-gray-100 rounded-lg'}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0.5">
        {renderFields(opqrstFields, 'Hx Present Illness')}
        {renderFields(sampleFields, 'Past Medical Hx')}
      </div>
    </div>
  );
};

// Custom Module Definitions
export const CUSTOM_MODULES = {
  'stacked-row': {
    id: 'stacked-row',
    name: '',
    description: 'Create a row with a single item and multiple expandable objectives, each with their own score, value, and critical fields',
    defaultData: {
      type: 'row',
      text: '',
      item: '',
      objective: '',
      notes: '',
      score: '',
      value: '',
      critical: false,
      moduleType: 'stacked-row',
      stackedObjectives: [
        { objective: '', notes: '', score: '', value: '', critical: false }
      ]
    },
    component: null // No component needed - handled directly in ScenarioEditor
  },
  'mental-status': {
    id: 'mental-status',
    name: 'Mental Status',
    description: 'Assess mental status with Person, Place, Time checkboxes and Chief Complaint checkbox with text entry',
    defaultData: {
      item: 'Mental Status',
      objective: 'Assess patient\'s mental status and orientation',
      notes: '',
      score: '',
      value: '',
      critical: false,
      moduleType: 'mental-status',
      moduleData: {
        Person: false,
        Place: false,
        Time: false,
        'C/C_checked': false,
        'C/C_text': ''
      }
    },
    component: MentalStatusModule
  },
  'vital-signs': {
    id: 'vital-signs',
    name: 'Vital Signs',
    description: 'Record vital signs with checkboxes and value entry for BP, HR, RR, LS, SPO2, PUPILS, BGL, JVD, P. Edema',
    defaultData: {
      type: 'sub-header',
      text: 'Vital Signs',
      item: '',
      objective: '',
      notes: '',
      score: '',
      value: '',
      critical: false,
      moduleType: 'vital-signs',
      moduleData: {
        'BP_checked': false,
        'BP_text': '',
        'HR_checked': false,
        'HR_text': '',
        'RR_checked': false,
        'RR_text': '',
        'LS_checked': false,
        'LS_text': '',
        'SPO2_checked': false,
        'SPO2_text': '',
        'PUPILS_checked': false,
        'PUPILS_text': '',
        'BGL_checked': false,
        'BGL_text': '',
        'JVD_checked': false,
        'JVD_text': '',
        'P. Edema_checked': false,
        'P. Edema_text': ''
      }
    },
    component: VitalSignsModule
  },
  'subheading': {
    id: 'subheading',
    name: 'Subheading',
    description: 'Enter a subheading for the module',
    defaultData: {
      subheading_text: ''
    },
    component: SubheadingModule
  },
  'opqrst-sample': {
    id: 'opqrst-sample',
    name: 'OPQRST & SAMPLE',
    description: 'Combined OPQRST and SAMPLE assessment with checkboxes',
    defaultData: {
      type: 'sub-header',
      text: 'History',
      item: '',
      objective: '',
      notes: '',
      score: '',
      value: '',
      critical: false,
      moduleType: 'opqrst-sample',
      moduleData: {
        'O_checked': false, 'O_text': '',
        'P_checked': false, 'P_text': '',
        'Q_checked': false, 'Q_text': '',
        'R_checked': false, 'R_text': '',
        'S_checked': false, 'S_text': '',
        'T_checked': false, 'T_text': '',
        'S_sample_checked': false, 'S_sample_text': '',
        'A_checked': false, 'A_text': '',
        'M_checked': false, 'M_text': '',
        'P_past_checked': false, 'P_past_text': '',
        'L_checked': false, 'L_text': '',
        'E_checked': false, 'E_text': ''
      }
    },
    component: OPQRSTSAMPLEModule
  }
};

// Get all available modules
export const getAvailableModules = () => {
  return Object.values(CUSTOM_MODULES);
};

// Get module by ID
export const getModuleById = (id) => {
  return CUSTOM_MODULES[id];
};

// Check if a row is a custom module
export const isCustomModule = (row) => {
  return row && row.moduleType && CUSTOM_MODULES[row.moduleType];
};

// Render custom module content
export const renderCustomModule = (row, onChange, isPreview = false, editingProps = {}) => {
  if (!isCustomModule(row)) return null;
  
  const moduleConfig = CUSTOM_MODULES[row.moduleType];
  if (!moduleConfig) return null;
  
  const ModuleComponent = moduleConfig.component;
  
  return (
    <ModuleComponent
      data={row.moduleData || {}}
      onChange={(newData) => {
        if (onChange) {
          onChange('moduleData', newData);
        }
      }}
      isPreview={isPreview}
      {...editingProps}
    />
  );
};
